<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { router, Link } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import Button from 'primevue/button';
import Card from 'primevue/card';
import Menu from 'primevue/menu';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import ColorPicker from 'primevue/colorpicker';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { useForm } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    projects: Array,
});

// State management
const showCreateDialog = ref(false);
const showEditDialog = ref(false);
const selectedProject = ref(null);
const menuRefs = ref({});

// Color states for ColorPicker components
const createColor = ref('3B82F6');
const editColor = ref('3B82F6');

// Forms
const createForm = useForm({
    name: '',
    color: '#3B82F6',
});

const editForm = useForm({
    name: '',
    color: '',
});

// Menu items for each project
const getMenuItems = (project) => [
    {
        label: $t('Edit'),
        icon: 'pi pi-pencil',
        command: () => openEditDialog(project)
    },
    {
        label: $t('Delete'),
        icon: 'pi pi-trash',
        class: 'text-red-500',
        command: () => confirmDelete(project)
    }
];

// Functions for color updates
const updateCreateColor = (value) => {
    createForm.color = '#' + value;
};

const updateEditColor = (value) => {
    editForm.color = '#' + value;
};

// Functions
const openCreateDialog = () => {
    createForm.reset();
    createForm.color = '#3B82F6';
    createColor.value = '3B82F6';
    showCreateDialog.value = true;
};

const openEditDialog = (project) => {
    selectedProject.value = project;
    editForm.name = project.name;
    editForm.color = project.color;
    editColor.value = project.color?.replace('#', '') || '3B82F6';
    showEditDialog.value = true;
};

const createProject = () => {
    showLoading();
    createForm.post(route('client.projects.store'), {
        preserveScroll: true,
        onSuccess: () => {
            hideLoading();
            showCreateDialog.value = false;
            createForm.reset();
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Project created successfully'), 
                life: 3000 
            });
        },
        onError: () => {
            hideLoading();
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: $t('Failed to create project'), 
                life: 3000 
            });
        }
    });
};

const updateProject = () => {
    showLoading();
    editForm.put(route('client.projects.update', selectedProject.value.id), {
        preserveScroll: true,
        onSuccess: () => {
            hideLoading();
            showEditDialog.value = false;
            selectedProject.value = null;
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Project updated successfully'), 
                life: 3000 
            });
        },
        onError: () => {
            hideLoading();
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: $t('Failed to update project'), 
                life: 3000 
            });
        }
    });
};

const confirmDelete = (project) => {
    if (confirm($t('Are you sure you want to delete this project?'))) {
        deleteProject(project);
    }
};

const deleteProject = (project) => {
    showLoading();
    router.delete(route('client.projects.destroy', project.id), {
        preserveScroll: true,
        onSuccess: () => {
            hideLoading();
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Project deleted successfully'), 
                life: 3000 
            });
        },
        onError: () => {
            hideLoading();
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: $t('Failed to delete project'), 
                life: 3000 
            });
        }
    });
};

const toggleMenu = (event, projectId) => {
    if (menuRefs.value[projectId]) {
        menuRefs.value[projectId].toggle(event);
    }
};

// Computed
const getProjectInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
};
</script>

<template>
    <Toast />
    
    <!-- Page Header -->
    <div class="surface-0 border-surface-200 border rounded-lg p-6 mb-4">
        <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
                <div class="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg">
                    <i class="pi pi-folder text-primary-600 text-2xl"></i>
                </div>
                <div>
                    <h1 class="text-2xl font-bold text-surface-900">{{ $t('Your projects') }}</h1>
                    <p class="text-sm text-surface-600">{{ $t('Find here your selections') }}</p>
                </div>
            </div>
            <Button 
                icon="pi pi-plus" 
                :label="$t('New Project')"
                @click="openCreateDialog"
                severity="primary"
            />
        </div>
    </div>

    <!-- Projects Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div v-for="project in projects" :key="project.id" class="relative">
            <Card class="hover:shadow-lg transition-shadow duration-200">
                <template #content>
                    <Link 
                        :href="route('client.sites.list', { _query: { 'filter[project]': project.id } })"
                        class="block"
                    >
                        <div class="flex items-center gap-3 mb-3">
                            <div 
                                class="flex items-center justify-center w-12 h-12 rounded-lg text-white font-semibold"
                                :style="{ backgroundColor: project.color }"
                            >
                                {{ getProjectInitials(project.name) }}
                            </div>
                            <div class="flex-1">
                                <h3 class="font-semibold text-surface-900 hover:text-primary-600 transition-colors">
                                    {{ project.name }}
                                </h3>
                                <p class="text-sm text-surface-600">
                                    {{ project.sites_count }} 
                                    {{ project.sites_count === 1 ? $t('website') : $t('websites') }}
                                </p>
                            </div>
                        </div>
                    </Link>
                    <div class="flex justify-end">
                        <Button 
                            icon="pi pi-ellipsis-v" 
                            text 
                            rounded 
                            severity="secondary"
                            @click="toggleMenu($event, project.id)"
                            size="small"
                        />
                        <Menu 
                            :ref="el => menuRefs[project.id] = el"
                            :model="getMenuItems(project)" 
                            :popup="true"
                        />
                    </div>
                </template>
            </Card>
        </div>

        <!-- Empty State -->
        <div v-if="projects.length === 0" class="col-span-full">
            <Card class="text-center py-12">
                <template #content>
                    <i class="pi pi-folder-open text-6xl text-surface-300 mb-4"></i>
                    <h3 class="text-xl font-semibold text-surface-700 mb-2">{{ $t('No projects yet') }}</h3>
                    <p class="text-surface-600 mb-4">{{ $t('Create your first project to organize your sites') }}</p>
                    <Button 
                        icon="pi pi-plus" 
                        :label="$t('Create Project')"
                        @click="openCreateDialog"
                        severity="primary"
                    />
                </template>
            </Card>
        </div>
    </div>

    <!-- Create Project Dialog -->
    <Dialog 
        v-model:visible="showCreateDialog" 
        :header="$t('New Project')"
        :modal="true"
        :style="{ width: '450px' }"
        :closable="!createForm.processing"
    >
        <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-2">
                <label for="create-name" class="font-semibold">{{ $t('Project name') }}</label>
                <InputText 
                    id="create-name"
                    v-model="createForm.name" 
                    :placeholder="$t('Enter project name')"
                    class="w-full"
                    :disabled="createForm.processing"
                    :class="{ 'p-invalid': createForm.errors.name }"
                />
                <small v-if="createForm.errors.name" class="p-error">{{ createForm.errors.name }}</small>
            </div>
            <div class="flex flex-col gap-2">
                <label for="create-color" class="font-semibold">{{ $t('Color') }}</label>
                <div class="flex items-center gap-3">
                    <ColorPicker 
                        id="create-color"
                        v-model="createColor"
                        @update:modelValue="updateCreateColor"
                        :disabled="createForm.processing"
                        format="hex"
                    />
                    <div 
                        class="w-12 h-12 rounded-lg border border-surface-300"
                        :style="{ backgroundColor: createForm.color }"
                    ></div>
                </div>
                <small v-if="createForm.errors.color" class="p-error">{{ createForm.errors.color }}</small>
            </div>
        </div>
        <template #footer>
            <Button 
                :label="$t('Cancel')" 
                text 
                @click="showCreateDialog = false"
                :disabled="createForm.processing"
            />
            <Button 
                :label="$t('Save')" 
                @click="createProject"
                :loading="createForm.processing"
                severity="primary"
            />
        </template>
    </Dialog>

    <!-- Edit Project Dialog -->
    <Dialog 
        v-model:visible="showEditDialog" 
        :header="$t('Edit Project')"
        :modal="true"
        :style="{ width: '450px' }"
        :closable="!editForm.processing"
    >
        <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-2">
                <label for="edit-name" class="font-semibold">{{ $t('Project name') }}</label>
                <InputText 
                    id="edit-name"
                    v-model="editForm.name" 
                    :placeholder="$t('Enter project name')"
                    class="w-full"
                    :disabled="editForm.processing"
                    :class="{ 'p-invalid': editForm.errors.name }"
                />
                <small v-if="editForm.errors.name" class="p-error">{{ editForm.errors.name }}</small>
            </div>
            <div class="flex flex-col gap-2">
                <label for="edit-color" class="font-semibold">{{ $t('Color') }}</label>
                <div class="flex items-center gap-3">
                    <ColorPicker 
                        id="edit-color"
                        v-model="editColor"
                        @update:modelValue="updateEditColor"
                        :disabled="editForm.processing"
                        format="hex"
                    />
                    <div 
                        class="w-12 h-12 rounded-lg border border-surface-300"
                        :style="{ backgroundColor: editForm.color }"
                    ></div>
                </div>
                <small v-if="editForm.errors.color" class="p-error">{{ editForm.errors.color }}</small>
            </div>
        </div>
        <template #footer>
            <Button 
                :label="$t('Cancel')" 
                text 
                @click="showEditDialog = false"
                :disabled="editForm.processing"
            />
            <Button 
                :label="$t('Update')" 
                @click="updateProject"
                :loading="editForm.processing"
                severity="primary"
            />
        </template>
    </Dialog>
</template>